export type ScheduleConflictReason =
  | "OVERLAP_SAME_SITE"
  | "OVERLAP_OTHER_SITE"
  | "ZERO_DURATION";

export interface IScheduleConflict {
  employeeId: number;
  day: number;
  localId?: number;
  localName?: string;
  start: string;
  end: string;
  reason: ScheduleConflictReason;
}

const DAY_NAMES: Record<number, string> = {
  1: "Lunes",
  2: "Martes",
  3: "Miércoles",
  4: "Jueves",
  5: "Viernes",
  6: "Sábado",
  7: "Domingo",
};

/** Mensaje legible de un conflicto individual (sin prefijo de empleado). */
export const describeScheduleConflict = (c: IScheduleConflict): string => {
  const range = `${DAY_NAMES[c.day] ?? `Día ${c.day}`} ${c.start}–${c.end}`;
  switch (c.reason) {
    case "OVERLAP_SAME_SITE":
      return `${range} se cruza con otra franja del mismo día`;
    case "OVERLAP_OTHER_SITE":
      return `${range} se cruza con su horario en ${c.localName || "otra sede"}`;
    case "ZERO_DURATION":
      return `${range} no es un turno válido (inicio y fin iguales)`;
    default:
      return `${range} tiene un conflicto de horario`;
  }
};

/**
 * Traduce el cuerpo de error del backend (400 SCHEDULE_CONFLICT / INVALID_SCHEDULE)
 * a un mensaje legible. Devuelve null si el error no es de horarios.
 * `employeeNames` (opcional) permite anteponer el nombre en cargas masivas.
 */
export const adaptScheduleConflictError = (
  body: any,
  employeeNames?: Record<number, string>
): string | null => {
  if (!body || typeof body !== "object") return null;
  if (body.code === "INVALID_SCHEDULE") {
    return body.message || "Horario inválido";
  }
  if (body.code !== "SCHEDULE_CONFLICT") return null;
  const conflicts: IScheduleConflict[] = Array.isArray(body.conflicts)
    ? body.conflicts
    : [];
  if (conflicts.length === 0) return body.message || "Conflicto de horarios";
  const multi = new Set(conflicts.map((c) => c.employeeId)).size > 1;
  return conflicts
    .map((c) => {
      const text = describeScheduleConflict(c);
      if (!multi) return text;
      const name = employeeNames?.[c.employeeId] || `Empleado #${c.employeeId}`;
      return `${name}: ${text}`;
    })
    .join("; ");
};
